import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { fmtUSD } from "@/lib/format";
import {
  Smartphone, Building2, Network, Wallet, ArrowRight,
  Play, RotateCcw, CheckCircle2, DollarSign, TrendingUp,
  Sparkles, Zap,
} from "lucide-react";

const KACYBER_RATE = 0.02;
const PLATFORM_RATE = 0.028;

type Phase = "idle" | "initiate" | "route" | "process" | "callback" | "split" | "done";

const PHASE_LABELS: Record<Phase, string> = {
  idle:      "Press Run to process a payment",
  initiate:  "Customer pays via BRECCSPay",
  route:     "BRECCSPay routes to KaCyberPay",
  process:   "KaCyberPay pushes MoMo prompt to customer",
  callback:  "KaCyberPay confirms · callback to BRECCSPay",
  split:     "Revenue split · both wallets credited",
  done:      "Transaction complete · settlement queued",
};

interface TxnRecord {
  id: string;
  amount: number;
  kacyberRevenue: number;
  tricampusRevenue: number;
  merchantReceives: number;
  customerFee: number;
}

export function RevenueFlowDemo() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [amount, setAmount] = useState(100000);
  const [kacyberWallet, setKacyberWallet] = useState(0);
  const [tricampusWallet, setTricampusWallet] = useState(0);
  const [txnCount, setTxnCount] = useState(0);
  const [history, setHistory] = useState<TxnRecord[]>([]);
  const [particles, setParticles] = useState<number[]>([]);
  const timers = useRef<number[]>([]);

  const kacyberCut = Math.round(amount * KACYBER_RATE);
  const tricampusCut = Math.round(amount * (PLATFORM_RATE - KACYBER_RATE));
  const customerFee = Math.round(amount * PLATFORM_RATE);
  const merchantReceives = amount;

  const reset = useCallback(() => {
    timers.current.forEach(t => window.clearTimeout(t));
    timers.current = [];
    setPhase("idle");
    setKacyberWallet(0);
    setTricampusWallet(0);
    setTxnCount(0);
    setHistory([]);
    setParticles([]);
  }, []);

  const run = useCallback(() => {
    timers.current.forEach(t => window.clearTimeout(t));
    timers.current = [];
    setPhase("initiate");

    const seq: Array<[number, () => void]> = [];
    let t = 0;
    const at = (ms: number, fn: () => void) => { t += ms; seq.push([t, fn]); };

    at(600,  () => setPhase("route"));
    at(700,  () => { setPhase("process"); setParticles([1]); });
    at(600,  () => setParticles([1, 2]));
    at(500,  () => setPhase("callback"));
    at(700,  () => { setPhase("split"); setParticles([1, 2, 3]); });
    at(800,  () => {
      setKacyberWallet(w => w + kacyberCut);
      setTricampusWallet(w => w + tricampusCut);
      setTxnCount(c => c + 1);
      setHistory(h => [
        {
          id: `TXN-${String(txnCount + 1).padStart(4, "0")}`,
          amount,
          kacyberRevenue: kacyberCut,
          tricampusRevenue: tricampusCut,
          merchantReceives,
          customerFee,
        },
        ...h,
      ].slice(0, 6));
    });
    at(400,  () => setPhase("done"));
    at(1200, () => setPhase("idle"));

    seq.forEach(([ms, fn]) => timers.current.push(window.setTimeout(fn, ms)));
  }, [amount, kacyberCut, tricampusCut, customerFee, merchantReceives, txnCount]);

  useEffect(() => () => { timers.current.forEach(t => window.clearTimeout(t)); }, []);

  const isActive = phase !== "idle";

  return (
    <Card className="overflow-hidden p-6 sm:p-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <Badge variant="outline">
            <Sparkles className="mr-1 h-3 w-3" /> Revenue Flow
          </Badge>
          <h3 className="mt-2 font-display text-2xl">How Both Wallets Make Money</h3>
          <p className="mt-1 max-w-xl text-sm text-muted-foreground">
            Every payment flows through BRECCSPay to KaCyberPay and back. KaCyberPay earns 2%
            as the payment rail. BRECCSPay earns 0.8% as the platform spread. Watch both wallets fill.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={reset}>
            <RotateCcw className="mr-1 h-3 w-3" />Reset
          </Button>
          <Button size="sm" className="bg-[var(--forest)] text-primary-foreground hover:opacity-90" onClick={run}>
            <Play className="mr-1 h-3 w-3" />Run transaction
          </Button>
        </div>
      </div>

      {/* Amount selector */}
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <span className="text-xs uppercase tracking-wider text-muted-foreground">Amount</span>
        {[10000, 50000, 100000, 500000].map((p) => (
          <Button
            key={p}
            variant={amount === p ? "default" : "outline"}
            size="sm"
            onClick={() => setAmount(p)}
            className="text-xs"
          >
            {fmtUSD(p)}
          </Button>
        ))}
      </div>

      {/* Flow visualization */}
      <div className="relative rounded-2xl border bg-gradient-to-br from-muted/30 to-muted/10 p-6 sm:p-8">
        {/* Phase indicator */}
        <div className="mb-6 flex items-center gap-2">
          <div className={`h-2 w-2 rounded-full transition ${isActive ? "bg-[var(--forest)] animate-pulse" : "bg-muted-foreground/30"}`} />
          <span className="text-sm text-muted-foreground">{PHASE_LABELS[phase]}</span>
        </div>

        {/* Nodes */}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {/* Customer */}
          <FlowNode
            icon={Smartphone}
            label="Customer"
            sub="Parent pays"
            active={phase === "initiate" || phase === "process"}
            tone="blue"
          />

          {/* BRECCSPay */}
          <FlowNode
            icon={Building2}
            label="BRECCSPay"
            sub="Platform · 0.8%"
            active={phase === "initiate" || phase === "route" || phase === "callback" || phase === "split"}
            tone="forest"
          />

          {/* KaCyberPay */}
          <FlowNode
            icon={Network}
            label="KaCyberPay"
            sub="Rail · 2.0%"
            active={phase === "route" || phase === "process" || phase === "callback"}
            tone="orange"
          />

          {/* Merchant */}
          <FlowNode
            icon={Building2}
            label="Merchant"
            sub="School receives"
            active={phase === "split" || phase === "done"}
            tone="purple"
          />
        </div>

        {/* Animated arrows between nodes */}
        <div className="mt-2 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <FlowArrow active={phase === "initiate" || phase === "route"} delay={0} />
          <FlowArrow active={phase === "route" || phase === "process"} delay={0.1} />
          <FlowArrow active={phase === "callback"} delay={0.2} reverse />
          <FlowArrow active={phase === "split" || phase === "done"} delay={0.3} />
        </div>

        {/* Money particles */}
        <AnimatePresence>
          {particles.includes(1) && (
            <motion.div
              className="absolute left-[12%] top-[40%] text-lg"
              initial={{ opacity: 0, x: 0 }}
              animate={{ opacity: [0, 1, 1, 0], x: 80 }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <DollarSign className="h-5 w-5 text-amber-500" />
            </motion.div>
          )}
          {particles.includes(2) && (
            <motion.div
              className="absolute left-[37%] top-[35%] text-lg"
              initial={{ opacity: 0, x: 0 }}
              animate={{ opacity: [0, 1, 1, 0], x: 80 }}
              transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
            >
              <DollarSign className="h-5 w-5 text-orange-500" />
            </motion.div>
          )}
          {particles.includes(3) && (
            <motion.div
              className="absolute left-[62%] top-[40%] text-lg"
              initial={{ opacity: 0, x: 0 }}
              animate={{ opacity: [0, 1, 1, 0], x: 80 }}
              transition={{ duration: 1.2, repeat: Infinity, delay: 0.5 }}
            >
              <DollarSign className="h-5 w-5 text-emerald-500" />
            </motion.div>
          )}
        </AnimatePresence>

        {/* Wallet balances */}
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {/* KaCyberPay wallet */}
          <WalletDisplay
            name="KaCyberPay Revenue"
            subtitle="2% provider fee per transaction"
            balance={kacyberWallet}
            txnCount={txnCount}
            tone="orange"
            icon={Network}
            highlighted={phase === "callback" || phase === "split"}
          />

          {/* BRECCSPay wallet */}
          <WalletDisplay
            name="BRECCSPay Revenue"
            subtitle="0.8% platform spread per transaction"
            balance={tricampusWallet}
            txnCount={txnCount}
            tone="forest"
            icon={Wallet}
            highlighted={phase === "split" || phase === "done"}
          />
        </div>
      </div>

      {/* Per-transaction breakdown */}
      <div className="mt-4 grid gap-3 sm:grid-cols-4">
        <BreakdownTile label="Customer pays" value={fmtUSD(amount + customerFee)} tone="blue" />
        <BreakdownTile label="KaCyberPay earns" value={fmtUSD(kacyberCut)} sub="2.0% rail fee" tone="orange" />
        <BreakdownTile label="BRECCSPay earns" value={fmtUSD(tricampusCut)} sub="0.8% spread" tone="forest" />
        <BreakdownTile label="Merchant receives" value={fmtUSD(merchantReceives)} sub="Settled to wallet" tone="purple" />
      </div>

      {/* Transaction history */}
      {history.length > 0 && (
        <div className="mt-4">
          <div className="mb-2 text-xs uppercase tracking-wider text-muted-foreground">Recent transactions</div>
          <div className="overflow-hidden rounded-lg border">
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Txn ID</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                  <th className="px-3 py-2 text-right">KaCyberPay</th>
                  <th className="px-3 py-2 text-right">BRECCSPay</th>
                  <th className="px-3 py-2 text-right">Merchant</th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence initial={false}>
                  {history.map((t) => (
                    <motion.tr
                      key={t.id}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0 }}
                      className="border-t"
                    >
                      <td className="px-3 py-2 font-mono text-[11px]">{t.id}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtUSD(t.amount)}</td>
                      <td className="px-3 py-2 text-right font-mono text-orange-600">+{fmtUSD(t.kacyberRevenue)}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-600">+{fmtUSD(t.tricampusRevenue)}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmtUSD(t.merchantReceives)}</td>
                    </motion.tr>
                  ))}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Combined revenue summary */}
      {txnCount > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 flex flex-wrap items-center gap-4 rounded-xl border bg-gradient-to-r from-emerald-50 to-amber-50 p-4"
        >
          <div className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[var(--forest)]" />
            <span className="text-sm font-medium">
              {txnCount} transaction{txnCount !== 1 ? "s" : ""} processed
            </span>
          </div>
          <div className="ml-auto flex gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">KaCyberPay total</div>
              <div className="font-display text-lg text-orange-600">{fmtUSD(kacyberWallet)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">BRECCSPay total</div>
              <div className="font-display text-lg text-emerald-600">{fmtUSD(tricampusWallet)}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Combined revenue</div>
              <div className="font-display text-lg text-[var(--forest)]">{fmtUSD(kacyberWallet + tricampusWallet)}</div>
            </div>
          </div>
        </motion.div>
      )}
    </Card>
  );
}

/* ---------- Sub-components ---------- */

const TONE_STYLES: Record<string, { ring: string; bg: string; text: string }> = {
  blue:   { ring: "ring-blue-400/50",   bg: "bg-blue-500/10",   text: "text-blue-600" },
  forest: { ring: "ring-emerald-400/50",bg: "bg-emerald-500/10",text: "text-emerald-600" },
  orange: { ring: "ring-orange-400/50", bg: "bg-orange-500/10", text: "text-orange-600" },
  purple: { ring: "ring-purple-400/50", bg: "bg-purple-500/10", text: "text-purple-600" },
};

function FlowNode({
  icon: Icon, label, sub, active, tone,
}: {
  icon: typeof Smartphone;
  label: string;
  sub: string;
  active: boolean;
  tone: "blue" | "forest" | "orange" | "purple";
}) {
  const ts = TONE_STYLES[tone];
  return (
    <motion.div
      animate={{ scale: active ? 1.05 : 1 }}
      transition={{ duration: 0.3 }}
      className={`flex flex-col items-center rounded-xl border p-3 text-center transition ${
        active ? `${ts.bg} ${ts.ring} ring-2` : "border-border bg-background"
      }`}
    >
      <div className={`grid h-10 w-10 place-items-center rounded-full ${active ? ts.bg : "bg-muted"}`}>
        <Icon className={`h-5 w-5 ${active ? ts.text : "text-muted-foreground"}`} />
      </div>
      <div className="mt-2 text-xs font-medium">{label}</div>
      <div className="text-[10px] text-muted-foreground">{sub}</div>
    </motion.div>
  );
}

function FlowArrow({ active, delay, reverse }: { active: boolean; delay: number; reverse?: boolean }) {
  return (
    <div className="flex items-center justify-center">
      <motion.div
        animate={{
          opacity: active ? 1 : 0.2,
          x: active ? (reverse ? -5 : 5) : 0,
        }}
        transition={{ duration: 0.4, repeat: active ? Infinity : 0, repeatType: "reverse", delay }}
      >
        {reverse ? (
          <ArrowRight className="h-4 w-4 rotate-180 text-[var(--forest)]" />
        ) : (
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
        )}
      </motion.div>
    </div>
  );
}

function WalletDisplay({
  name, subtitle, balance, txnCount, tone, icon: Icon, highlighted,
}: {
  name: string;
  subtitle: string;
  balance: number;
  txnCount: number;
  tone: "orange" | "forest";
  icon: typeof Wallet;
  highlighted: boolean;
}) {
  const ts = TONE_STYLES[tone];
  return (
    <motion.div
      animate={{
        scale: highlighted ? 1.02 : 1,
        boxShadow: highlighted ? "0 0 20px rgba(34,197,94,0.15)" : "0 0 0px rgba(0,0,0,0)",
      }}
      transition={{ duration: 0.3 }}
      className={`rounded-xl border p-4 transition ${highlighted ? ts.ring + " ring-2" : "border-border"}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`grid h-8 w-8 place-items-center rounded-lg ${ts.bg}`}>
            <Icon className={`h-4 w-4 ${ts.text}`} />
          </div>
          <div>
            <div className="text-sm font-medium">{name}</div>
            <div className="text-[10px] text-muted-foreground">{subtitle}</div>
          </div>
        </div>
        <Badge variant="outline" className="text-[10px]">
          {txnCount} txn{txnCount !== 1 ? "s" : ""}
        </Badge>
      </div>
      <motion.div
        key={balance}
        initial={{ scale: 1.1, color: tone === "forest" ? "#059669" : "#ea580c" }}
        animate={{ scale: 1, color: "inherit" }}
        transition={{ duration: 0.4 }}
        className="mt-3 font-display text-2xl"
      >
        {fmtUSD(balance)}
      </motion.div>
      <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
        <motion.div
          className={`h-full rounded-full ${tone === "forest" ? "bg-emerald-500" : "bg-orange-500"}`}
          initial={{ width: 0 }}
          animate={{ width: `${Math.min(100, (balance / 500000) * 100)}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>
    </motion.div>
  );
}

function BreakdownTile({
  label, value, sub, tone,
}: {
  label: string;
  value: string;
  sub?: string;
  tone: "blue" | "orange" | "forest" | "purple";
}) {
  const ts = TONE_STYLES[tone];
  return (
    <div className="rounded-xl border p-3">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`mt-1 font-display text-lg ${ts.text}`}>{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}
